import { call, delay, put, select, spawn, take } from 'redux-saga/effects';
import { channel } from 'redux-saga';
import { IntelligentAssignmentSummary } from '../constants';
import { getAttachmentIterationInfo, getPhotoCaptureContextId } from '../selectors';
import { attachmentIteration } from './intelligentPhotoCaptureEstimatorUtilities';
/**
 * Creates a batch processor chain for efficient processing of items in batches
 * with controlled concurrency and sequential processing steps
 * @template T - The type of items to process
 * @returns A batch processor chain API for managing batch processing
 */
export function createBatchProcessorChain() {
    const processors = new Map();
    const completionChannels = new Map();
    const queueProcessorStarted = new Map();
    let batchIdCounter = 0;
    /**
     * Adds an item to be processed in a batch
     * @param item - The item to add to the batch
     * @param config - Configuration for batch processing
     * @yields Effects for Redux-Saga
     */
    function* addItemToProcessor(item, config) {
        // Create unique key for processor chain
        const processorKey = config.processors.map(p => p.name).join('-');
        // Get or initialize state
        let state = processors.get(processorKey);
        if (!state) {
            state = { currentBatch: [], queue: [], activeCount: 0 };
            processors.set(processorKey, state);
            // Initialize processor if needed
            if (!queueProcessorStarted.get(processorKey)) {
                const completionChannel = channel();
                completionChannels.set(processorKey, completionChannel);
                queueProcessorStarted.set(processorKey, true);
                // Start background processor
                yield spawn(queueProcessor, config, processorKey, completionChannel);
            }
        }
        // Add item to current batch
        state.currentBatch.push(item);
        // Check if batch is complete
        if (state.currentBatch.length >= config.batchSize) {
            state.queue.push([...state.currentBatch]);
            state.currentBatch = [];
            processors.set(processorKey, state);
            yield call(tryStartNextBatch, config, processorKey);
        }
        else {
            processors.set(processorKey, state);
        }
    }
    /**
     * Background processor that listens for batch completions
     * @param config - Configuration for batch processing
     * @param processorKey - Unique key for the processor chain
     * @param completionChannel - Channel for batch completion events
     * @yields Effects for Redux-Saga
     */
    function* queueProcessor(config, processorKey, completionChannel) {
        while (true) {
            // Wait for batch completion signal
            yield take(completionChannel);
            // Update state and try to start next batch
            const state = processors.get(processorKey);
            if (state) {
                state.activeCount--;
                processors.set(processorKey, state);
            }
            // Try to start next batch from queue
            yield call(tryStartNextBatch, config, processorKey);
        }
    }
    /**
     * Starts processing batches from queue if capacity allows
     * @param config - Configuration for batch processing
     * @param processorKey - Unique key for the processor chain
     * @yields Effects for Redux-Saga
     */
    function* tryStartNextBatch(config, processorKey) {
        const state = processors.get(processorKey);
        // Process as many batches as capacity allows
        while (state && state.activeCount < config.maxConcurrentBatches && state.queue.length > 0) {
            const iterationId = (yield call(getAttachmentsIterationInfo));
            const batchId = (yield call(generateBatchId, ++batchIdCounter, iterationId));
            const batch = state.queue.shift();
            state.activeCount++;
            processors.set(processorKey, state);
            // Start processing this batch
            const completionChannel = completionChannels.get(processorKey);
            yield spawn(processSingleBatch, batch, config, batchId, completionChannel);
        }
    }
    /**
     * Processes a single batch through all processors in the chain
     * @param batch - The batch of items to process
     * @param config - Configuration for batch processing
     * @param batchId - Unique identifier for this batch
     * @param completionChannel - Channel to signal batch completion
     * @yields Effects for Redux-Saga
     */
    function* processSingleBatch(batch, config, batchId, completionChannel) {
        const processorContext = {};
        try {
            // Run each processor in sequence
            for (const processor of config.processors) {
                // Skip if processor shouldn't execute
                if (processor.shouldExecute && !processor.shouldExecute(processorContext)) {
                    continue;
                }
                // Execute processor and store result
                const result = yield call(processor.fn, batch, batchId, processorContext);
                processorContext[processor.name] = result;
            }
        }
        catch (error) {
            // In the upcoming PR, we will add the corresponding code to handle error
            console.error(`Error processing batch ${batchId}:`, error);
        }
        finally {
            // Signal completion
            if (completionChannel) {
                yield put(completionChannel, batchId);
            }
        }
    }
    /**
     * Processes any remaining items that don't form a complete batch
     * @param config - Configuration for batch processing
     * @yields Effects for Redux-Saga
     */
    function* flushRemainingBatch(config) {
        const processorKey = config.processors.map(p => p.name).join('-');
        let state = processors.get(processorKey);
        // Nothing to flush
        if (!state || !state.currentBatch.length) {
            return;
        }
        state.queue.push([...state.currentBatch]);
        state.currentBatch = [];
        processors.set(processorKey, state);
        yield call(tryStartNextBatch, config, processorKey);
        // Wait for all active batches to complete
        state = processors.get(processorKey);
        while (state && (state.activeCount > 0 || state.queue.length > 0)) {
            yield delay(IntelligentAssignmentSummary.UPLOAD_STATUS_INTERVAL_MS);
            state = processors.get(processorKey);
        }
    }
    /**
     * Retrieves the current iteration ID for attachments being processed
     * @returns The iteration ID if found, otherwise null
     * @yields Effects for Redux-Saga to select state data
     */
    function* getAttachmentsIterationInfo() {
        const attachmentIterationInfo = yield select(getAttachmentIterationInfo);
        const currentIteration = attachmentIteration.getInProgressIteration(attachmentIterationInfo);
        return currentIteration ? currentIteration.iterationId : null;
    }
    /**
     * Generates a unique batch ID using context and iteration information
     * @param batchIdCounter - Counter to ensure uniqueness of batch ID
     * @param iterationId - The current iteration ID to include in the batch ID
     * @returns A unique batch ID string
     * @yields Effects for Redux-Saga to select context ID from state
     */
    function* generateBatchId(batchIdCounter, iterationId) {
        const contextId = (yield call(getContextId));
        return `${contextId}-iteration_${iterationId}-batch_${batchIdCounter}`;
    }
    /**
     * Retrieves the current photo capture context ID from state
     * @returns The context ID as a string
     * @yields Effects for Redux-Saga to select context ID from state
     */
    function* getContextId() {
        return yield select(getPhotoCaptureContextId);
    }
    /**
     * Clears a specific processor chain
     * @param name - The name of the processor to clear
     */
    function clearProcessor(name) {
        processors.delete(name);
        completionChannels.delete(name);
        queueProcessorStarted.delete(name);
    }
    /**
     * Clears all processor chains
     */
    function clearAll() {
        processors.clear();
        completionChannels.clear();
        queueProcessorStarted.clear();
        batchIdCounter = 0;
    }
    return {
        addItemToProcessor,
        flushRemainingBatch,
        clearProcessor,
        clearAll
    };
}
//# sourceMappingURL=intelligentPhotoCaptureBatchChain.js.map